// Serviço para gerenciar Push Notifications Web
import axios from 'axios';
import { isInLoginGracePeriod } from './api';

const isNotificationDebugEnabled = import.meta.env.VITE_DEBUG_NOTIFICATIONS === 'true';
const debugLog = (...args: unknown[]) => {
  if (isNotificationDebugEnabled) {
    console.debug(...args);
  }
};

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id?: string;
  user_id?: string;
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  url?: string;
  type?: NotificationType;
  data?: Record<string, unknown>;
  read?: boolean;
  read_at?: string | null;
  created_at?: string;
  updated_at?: string;
  timestamp?: number;
}

export interface NotificationSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

type RawNotification = Partial<Notification> & {
  payload?: unknown;
  readAt?: string;
  createdAt?: string;
  [key: string]: any;
};

interface RawNotificationHistoryResponse {
  notifications?: RawNotification[];
  count?: number;
}

export interface NotificationHistoryResult {
  notifications: Notification[];
  count: number;
}

class NotificationService {
  private notificationAPI = axios.create({
    baseURL: '/api/v1',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private normalizeNotification(raw: RawNotification): Notification {
    // Validar dados básicos
    if (!raw) {
      throw new Error('Invalid notification payload: raw is null or undefined');
    }

    // Permitir notificações sem título ou body, mas com valores padrão
    const title = raw.title || raw.Title || 'Notificação';
    const body = raw.body || raw.Body || '';

    if (!title && !body) {
      throw new Error('Invalid notification payload: missing both title and body');
    }

    const createdAt =
      typeof raw.created_at === 'string'
        ? raw.created_at
        : typeof raw.createdAt === 'string'
          ? raw.createdAt
          : undefined;

    const readAt =
      typeof raw.read_at === 'string'
        ? raw.read_at
        : typeof raw.readAt === 'string'
          ? raw.readAt
          : undefined;

    const dataCandidate = raw.data ?? raw.payload;
    const normalizedData =
      dataCandidate && typeof dataCandidate === 'object' && !Array.isArray(dataCandidate)
        ? (dataCandidate as Record<string, unknown>)
        : undefined;

    return {
      id: raw.id || raw.Id || '',
      user_id: raw.user_id || raw.UserId || '',
      title: title,
      body: body,
      icon: raw.icon || raw.Icon,
      badge: raw.badge || raw.Badge,
      tag: raw.tag || raw.Tag,
      url: raw.url || raw.Url,
      type: (raw.type || raw.Type || 'info') as NotificationType,
      data: normalizedData,
      read: raw.read ?? raw.Read ?? false,
      read_at: readAt ?? null,
      created_at: createdAt,
      updated_at: raw.updated_at || raw.UpdatedAt,
      timestamp: createdAt ? new Date(createdAt).getTime() : (raw.timestamp || Date.now()),
    };
  }

  constructor() {
    // Adicionar token nas requisições
    this.notificationAPI.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Tratar erros de autenticação
    this.notificationAPI.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Não invalidar token se estivermos no período de graça após login
          if (isInLoginGracePeriod()) {
            console.warn('[NotificationService] 401 durante período de graça após login, ignorando invalidação de token');
            return Promise.reject(error);
          }
          
          const isLoginPage = window.location.pathname === '/login';
          if (!isLoginPage) {
            const currentToken = localStorage.getItem('token');
            if (currentToken) {
              localStorage.removeItem('token');
              localStorage.removeItem('default_company_id');
              window.dispatchEvent(new CustomEvent('auth:token-invalid'));
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Verificar se o navegador suporta Push Notifications
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Verificar se as permissões já foram concedidas
   */
  async getPermissionStatus(): Promise<NotificationPermission> {
    return Notification.permission;
  }

  /**
   * Solicitar permissão para notificações
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications não são suportadas neste navegador');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  /**
   * Registrar Service Worker
   */
  async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Workers não são suportados');
    }

    try {
      // O VitePWA registra automaticamente o service worker
      // Apenas obter o registro existente
      let registration = await navigator.serviceWorker.getRegistration('/');
      if (!registration) {
        // Se não houver registro, aguardar um pouco e tentar novamente
        // (o VitePWA pode estar ainda registrando)
        await new Promise(resolve => setTimeout(resolve, 1000));
        registration = await navigator.serviceWorker.getRegistration('/');
      }

      if (!registration) {
        throw new Error('Service Worker não foi registrado pelo VitePWA. Certifique-se de que o build foi feito corretamente.');
      }

      debugLog('[NotificationService] Service Worker registrado:', registration);
      return registration;
    } catch (error) {
      debugLog('[NotificationService] Erro ao obter Service Worker:', error);
      throw error;
    }
  }

  /**
   * Obter subscription para Push Notifications
   */
  async getSubscription(): Promise<PushSubscription | null> {
    try {
      const registration = await this.registerServiceWorker();
      return registration.pushManager.getSubscription();
    } catch (error) {
      debugLog('[NotificationService] Não foi possível obter subscription (provavelmente SW ausente):', error);
      return null;
    }
  }

  /**
   * Criar subscription para Push Notifications
   */
  async subscribeToPush(): Promise<PushSubscription> {
    const registration = await this.registerServiceWorker();

    // Verificar se já existe subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Obter VAPID public key
      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
      if (!vapidPublicKey) {
        throw new Error('VITE_VAPID_PUBLIC_KEY não está configurada. Configure esta variável de ambiente.');
      }

      // Criar nova subscription
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey) as any,
      });
    }

    return subscription;
  }

  /**
   * Converter chave VAPID de base64 para Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Converter subscription para formato JSON
   */
  subscriptionToJSON(subscription: PushSubscription): NotificationSubscription {
    const encodeKey = (keyData: ArrayBuffer | null): string => {
      if (!keyData) {
        return '';
      }

      const bytes = new Uint8Array(keyData);
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return btoa(binary);
    };

    const key = subscription.getKey('p256dh');
    const auth = subscription.getKey('auth');

    return {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: encodeKey(key),
        auth: encodeKey(auth),
      },
    };
  }

  /**
   * Registrar subscription no backend
   */
  async registerSubscription(subscription: PushSubscription): Promise<void> {
    const subscriptionData = this.subscriptionToJSON(subscription);

    try {
      await this.notificationAPI.post('/notifications/subscribe', subscriptionData);
      debugLog('[NotificationService] Subscription registrada no backend');
    } catch (error) {
      console.error('[NotificationService] Erro ao registrar subscription:', error);
      throw error;
    }
  }

  /**
   * Cancelar subscription
   */
  async unsubscribeFromPush(): Promise<void> {
    const subscription = await this.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      await this.notificationAPI.post('/notifications/unsubscribe', {
        endpoint: subscription.endpoint,
      });
      debugLog('[NotificationService] Subscription cancelada');
    }
  }

  /**
   * Enviar notificação de teste
   */
  async sendTestNotification(): Promise<void> {
    await this.notificationAPI.post('/notifications/test', {
      title: 'Notificação de Teste',
      body: 'Esta é uma notificação de teste do Agenda House Keep',
      type: 'info',
    });
  }

  /**
   * Obter histórico de notificações
   */
  async getNotificationHistory(limit = 50, offset = 0): Promise<NotificationHistoryResult> {
    try {
      const response = await this.notificationAPI.get<RawNotificationHistoryResponse>('/notifications/history', {
        params: { limit, offset },
      });
      const rawNotifications = response.data.notifications ?? [];
      const notifications = rawNotifications.map((item) => this.normalizeNotification(item));

      return {
        notifications,
        count: response.data?.count ?? notifications.length,
      };
    } catch (error) {
      console.error('[NotificationService] Erro ao obter histórico:', error);
      return {
        notifications: [],
        count: 0,
      };
    }
  }

  /**
   * Marcar notificação como lida
   */
  async markAsRead(notificationId: string): Promise<void> {
    await this.notificationAPI.patch(`/notifications/${notificationId}/read`);
  }

  /**
   * Marcar todas as notificações como lidas
   */
  async markAllAsRead(): Promise<void> {
    await this.notificationAPI.post('/notifications/read-all');
  }

  /**
   * Deletar notificação
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await this.notificationAPI.delete(`/notifications/${notificationId}`);
  }

  /**
   * Obter contagem de notificações não lidas
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await this.notificationAPI.get<{ count?: number; unread_count?: number }>('/notifications/unread-count');
      // Retornar 0 se não houver resposta válida
      return response.data?.count ?? response.data?.unread_count ?? 0;
    } catch (error) {
      console.error('[NotificationService] Erro ao obter contagem de não lidas:', error);
      return 0;
    }
  }

  /**
   * Mostrar notificação local (para testes)
   */
  async showLocalNotification(notification: Notification): Promise<void> {
    if (!('Notification' in window)) {
      throw new Error('Notificações não são suportadas');
    }

    if (Notification.permission !== 'granted') {
      throw new Error('Permissão para notificações não foi concedida');
    }

    const registration = await this.registerServiceWorker();
    await registration.showNotification(notification.title, {
      body: notification.body,
      icon: notification.icon || '/favicon.ico',
      badge: notification.badge || '/favicon.ico',
      tag: notification.tag || 'agenda-notification',
      data: {
        url: notification.url || '/',
        timestamp: notification.timestamp || Date.now(),
        type: notification.type || 'info',
        ...notification.data,
      },
      vibrate: [200, 100, 200],
    } as any);
  }

  /**
   * Obter preferências de notificação do usuário
   */
  async getPreferences(): Promise<UserPreferences> {
    try {
      const response = await this.notificationAPI.get<UserPreferences>('/notifications/preferences');
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Erro ao obter preferências:', error);
      throw error;
    }
  }

  /**
   * Atualizar preferências de notificação do usuário
   */
  async updatePreferences(preferences: UpdatePreferencesRequest): Promise<UserPreferences> {
    try {
      const response = await this.notificationAPI.post<UserPreferences>('/notifications/preferences', preferences);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Erro ao atualizar preferências:', error);
      throw error;
    }
  }
}

export interface UserPreferences {
  id: string;
  user_id: string;
  channels: {
    web?: boolean;
    email?: boolean;
    sms?: boolean;
    push_mobile?: boolean;
  };
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  appointment_reminders: boolean;
  appointment_created: boolean;
  appointment_cancelled: boolean;
  payment_notifications: boolean;
  system_notifications: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpdatePreferencesRequest {
  channels?: {
    web?: boolean;
    email?: boolean;
    sms?: boolean;
    push_mobile?: boolean;
  };
  quiet_hours_start?: string | null; // formato "HH:MM"
  quiet_hours_end?: string | null; // formato "HH:MM"
  appointment_reminders?: boolean;
  appointment_created?: boolean;
  appointment_cancelled?: boolean;
  payment_notifications?: boolean;
  system_notifications?: boolean;
}

export const notificationService = new NotificationService();
export default notificationService;

